// ====================================================
// VidyaTrack — All Static / Legal / Company Pages
// Routes: /legal/:page  and  /company/:page
// ====================================================

import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiPhone, FiMail, FiMapPin } from "react-icons/fi";
import useSEO from "../../seo/useSEO";

// ── All page content ─────────────────────────────────────────
const PAGES = {

  /* ─── LEGAL ─────────────────────────────────────── */
  "privacy-policy": {
    title: "Privacy Policy",
    emoji: "🔒",
    lastUpdated: "May 25, 2025",
    sections: [
      { h:"1. Introduction", b:`VidyaTrack ("we", "our", or "us") is committed to protecting the privacy of students, teachers, parents, and school administrators. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use VidyaTrack.` },
      { h:"2. Information We Collect", b:`We collect information you provide directly when registering:
• Full name and email address
• Role (Admin, Teacher, Student, or Parent)
• School name and contact details
• Homework submissions (text notes and external links)
• Attendance records
• Academic performance data

We do NOT collect payment card details or sensitive financial information directly.` },
      { h:"3. How We Use Your Information", b:`We use the information we collect to:
• Provide and maintain the VidyaTrack platform
• Enable communication between teachers, students, and parents
• Track academic progress and attendance
• Send notifications about homework, notices, and events
• Improve our services and user experience
• Comply with legal obligations` },
      { h:"4. Data Storage & Security", b:`Your data is stored securely using Google Firebase (Firestore Database and Realtime Database), which uses industry-standard encryption at rest and in transit. All communications use HTTPS/TLS. Firebase Security Rules ensure users can only access data they are authorised to view.` },
      { h:"5. Data Sharing", b:`We do not sell, trade, or rent your personal information to third parties. We may share data with:
• Firebase/Google (our infrastructure provider)
• School administrators who manage your school's VidyaTrack account
• EmailJS (for sending contact form submissions)

We do not share personal data of students with any advertising networks.` },
      { h:"6. Children's Privacy", b:`VidyaTrack is designed for use in schools and may be used by students under 13 years of age. We collect only the minimum data necessary for educational purposes. Schools are responsible for obtaining appropriate parental consent before registering students.` },
      { h:"7. Data Retention", b:`We retain your data as long as your school's account is active. Schools may request deletion of their data at any time by contacting us at titaniumcode.np@gmail.com. Upon account closure, data is deleted within 30 days.` },
      { h:"8. Your Rights", b:`You have the right to:
• Access the personal data we hold about you
• Correct inaccurate data
• Request deletion of your data
• Object to data processing
• Data portability

To exercise these rights, contact your school administrator or email us at titaniumcode.np@gmail.com.` },
      { h:"9. Contact Us", b:`Email: titaniumcode.np@gmail.com\nPhone: +977-9804044517\nAddress: Kathmandu, Nepal` },
    ],
  },

  "terms-of-service": {
    title: "Terms of Service",
    emoji: "📄",
    lastUpdated: "May 25, 2025",
    sections: [
      { h:"1. Acceptance of Terms", b:`By accessing or using VidyaTrack, you agree to be bound by these Terms of Service. If you do not agree, please do not use our platform. These terms apply to all users including administrators, teachers, students, and parents.` },
      { h:"2. Description of Service", b:`VidyaTrack is a cloud-based school management platform providing tools for homework management, attendance tracking, notices, study materials, group work, academic calendars, and analytics. The platform is provided as a Software-as-a-Service (SaaS) subscription.` },
      { h:"3. Account Registration", b:`Schools must register through an authorised administrator. You are responsible for:
• Providing accurate registration information
• Maintaining the security of your account credentials
• All activities that occur under your account
• Notifying us immediately of any unauthorised account access

User accounts for teachers, students, and parents must be created by school administrators.` },
      { h:"4. Acceptable Use", b:`You agree not to:
• Use the platform for any unlawful purpose
• Upload harmful, offensive, or inappropriate content
• Attempt to gain unauthorised access to other users' data
• Reverse engineer or copy any part of the platform
• Use the platform to harass or harm any person
• Share your login credentials with unauthorised persons` },
      { h:"5. Subscription & Payment", b:`VidyaTrack is offered on a per-student, per-month subscription basis:
• Silver Plan: Rs. 149 per student per month (up to 300 students, 20 teachers)
• Gold Plan: Rs. 199 per student per month (up to 800 students, 40 teachers)
• Platinum Plan: Contact us for pricing (unlimited students & teachers)

Payment is due at the start of each billing month. Failure to pay may result in account suspension.` },
      { h:"6. Intellectual Property", b:`VidyaTrack and its original content, features, and functionality are owned by VidyaTrack and protected by copyright, trademark, and other intellectual property laws. You retain ownership of any content you upload to the platform.` },
      { h:"7. Limitation of Liability", b:`VidyaTrack shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the platform. Our total liability shall not exceed the amount paid in the 3 months preceding the claim.` },
      { h:"8. Termination", b:`Either party may terminate the service agreement with 30 days' written notice. We reserve the right to immediately suspend accounts that violate these terms. Upon termination, your data will be retained for 30 days before permanent deletion.` },
      { h:"9. Governing Law", b:`These Terms are governed by the laws of Nepal. Disputes shall be resolved through mutual negotiation. If unresolved, disputes shall be submitted to arbitration in Kathmandu, Nepal.` },
      { h:"10. Contact", b:`Email: titaniumcode.np@gmail.com\nPhone: +977-9804044517` },
    ],
  },

  "security": {
    title: "Security",
    emoji: "🛡️",
    lastUpdated: "May 25, 2025",
    sections: [
      { h:"Our Security Commitment", b:`At VidyaTrack, security is our top priority. We protect the academic data of students, teachers, and parents using industry-leading security practices.` },
      { h:"Infrastructure Security", b:`VidyaTrack is built on Google Firebase, one of the world's most secure cloud platforms:
• All data is encrypted at rest using AES-256 encryption
• All data in transit is protected by TLS 1.3
• Firebase data centres maintain SOC 2 Type II, ISO 27001 certification
• Automatic backups and 99.9% uptime SLA` },
      { h:"Authentication & Access Control", b:`• Email/password authentication via Firebase Auth
• Role-based access control (Admin, Teacher, Student, Parent)
• Firestore Security Rules enforce per-user data access
• Session tokens automatically invalidated on logout
• Password reset via secure email link only` },
      { h:"Data Access Controls", b:`• Students can only see their own homework, attendance, and materials
• Teachers can only manage their assigned classes
• Parents can only view their linked child's data
• Admins have school-wide access within their school only
• No cross-school data access is possible` },
      { h:"Reporting Security Issues", b:`If you discover a security vulnerability, please report it responsibly:\n\nEmail: titaniumcode.np@gmail.com\nSubject: [SECURITY] Vulnerability Report\n\nWe will acknowledge receipt within 24 hours. Please do not publicly disclose vulnerabilities until we have issued a fix.` },
    ],
  },

  "cookie-policy": {
    title: "Cookie Policy",
    emoji: "🍪",
    lastUpdated: "May 25, 2025",
    sections: [
      { h:"What Are Cookies?", b:`Cookies are small text files stored on your device by your web browser. VidyaTrack uses a minimal number of cookies to ensure the platform functions correctly.` },
      { h:"Cookies We Use", b:`Essential Cookies (cannot be disabled):
• Firebase Authentication token — keeps you logged in during your session
• Theme preference — remembers your light/dark mode preference

We do NOT use:
• Advertising or tracking cookies
• Third-party analytics cookies
• Social media cookies` },
      { h:"Managing Cookies", b:`You can control cookies through your browser settings. However, disabling essential cookies will prevent you from logging into VidyaTrack.` },
      { h:"Contact", b:`Email: titaniumcode.np@gmail.com` },
    ],
  },

  /* ─── COMPANY ────────────────────────────────────── */
  "about": {
    title: "About VidyaTrack",
    emoji: "🏫",
    lastUpdated: "May 25, 2025",
    sections: [
      { h:"Who We Are", b:`VidyaTrack is a Nepali EdTech startup building the smartest school management system for schools across Nepal. We believe every school — no matter how big or small — deserves modern, affordable digital tools that actually work.

We connect teachers, students, and parents in one unified platform so schools can focus on what matters most: education.` },
      { h:"Our Mission", b:`To make school management simple, transparent, and accessible for every school in Nepal. We are committed to reducing paperwork, improving communication, and giving teachers more time to teach.` },
      { h:"Our Vision", b:`A Nepal where every school runs digitally — where parents are always informed, teachers are empowered, and students are motivated. We envision VidyaTrack becoming the standard school management platform across South Asia.` },
      { h:"What We Offer", b:`• Homework management with submission tracking and grading
• Daily attendance marking and parent alerts
• School-wide notices and announcements
• Study material sharing via Google Drive links
• Group work and collaborative assignments
• Academic calendar with events and exams
• Real-time analytics and performance reports
• Role-based dashboards for Admin, Teacher, Student, and Parent` },
      { h:"Our Technology", b:`VidyaTrack is built using modern, reliable technology:
• React.js — fast, responsive user interface
• Firebase Firestore — real-time database
• Firebase Authentication — secure login
• Firebase Realtime Database — instant notifications
• Google Cloud infrastructure — 99.9% uptime

All data is stored securely in Google's cloud servers with encryption at rest and in transit.` },
      { h:"Get in Touch", b:`We would love to hear from you.\n\nEmail: titaniumcode.np@gmail.com\nPhone: +977-9804044517\nAddress: Kathmandu, Nepal` },
    ],
  },

  "blog": {
    title: "Blog",
    emoji: "📝",
    lastUpdated: "May 25, 2025",
    sections: [
      { h:"Introducing VidyaTrack — The Future of School Management in Nepal", b:`Published: May 25, 2025

We are excited to officially launch VidyaTrack — a complete digital school management platform designed specifically for schools in Nepal.

For too long, schools have relied on paper registers, WhatsApp groups, and manual phone calls to manage daily operations. VidyaTrack changes all of that with one unified platform that brings teachers, students, and parents together.

Key highlights of our launch:
• Smart homework assignment and online submission
• One-click daily attendance marking
• Real-time notices and announcements
• Google Drive-integrated study materials
• Mobile-friendly for students and parents
• Pricing starting at just Rs. 99 per student per month

We are currently onboarding pilot schools in Kathmandu. If your school wants to be part of our early adopter programme, contact us at titaniumcode.np@gmail.com.` },
      { h:"Why Digital School Management Matters", b:`Published: May 20, 2025

Research shows that schools with digital management systems see:
• 40% reduction in administrative workload for teachers
• 60% improvement in parent-school communication
• 35% improvement in homework submission rates
• Significant reduction in student attendance issues (due to instant alerts)

Nepal's education system is at a turning point. With internet access expanding rapidly and affordable smartphones becoming common, the time is right for digital transformation in our schools.

VidyaTrack is our contribution to this transformation.` },
      { h:"How VidyaTrack Handles Data Privacy for Students", b:`Published: May 15, 2025

One of the most common questions we receive from school administrators is: "How do you protect our students' data?"

Here is our answer:
• All data is stored on Google Firebase — one of the world's most secure cloud platforms
• Students can only see their own data — not other students' records
• Parents can only see their own child's data
• All communications are encrypted using TLS 1.3
• We never sell or share student data with third parties

Read our full Privacy Policy for complete details.` },
    ],
  },

  "careers": {
    title: "Careers",
    emoji: "💼",
    lastUpdated: "May 25, 2025",
    sections: [
      { h:"Join the VidyaTrack Team", b:`We are a small but passionate team building something that genuinely matters — digital tools that improve education for children across Nepal.

If you are excited about EdTech, love clean code, and want to make a real impact, we want to hear from you.` },
      { h:"Open Positions", b:`🧑‍💻 Frontend Developer (React.js)
Location: Kathmandu / Remote
Type: Full-time / Part-time

Requirements:
• 1+ years of React.js experience
• Familiarity with Firebase or similar real-time databases
• Passion for clean, user-friendly interfaces
• Good communication skills in Nepali and English

---

📱 Mobile Developer (React Native)
Location: Kathmandu / Remote
Type: Part-time / Freelance

Requirements:
• Experience with React Native
• Understanding of mobile UX design
• Firebase integration experience preferred

---

🎨 UI/UX Designer
Location: Kathmandu / Remote
Type: Part-time / Freelance

Requirements:
• Portfolio of clean, minimal interface designs
• Figma proficiency
• Understanding of educational software preferred

---

📣 Sales & Marketing Executive
Location: Kathmandu
Type: Full-time

Requirements:
• Experience in B2B sales (SaaS preferred)
• Strong network in Nepal's education sector
• Excellent communication skills in Nepali and English` },
      { h:"How to Apply", b:`Send your CV and a short note about why you want to join VidyaTrack to:\n\nEmail: titaniumcode.np@gmail.com\nSubject: [CAREERS] Position Name — Your Name\n\nWe review all applications and respond within 5 business days.` },
      { h:"Our Culture", b:`At VidyaTrack, we believe in:
• Moving fast and shipping things that work
• Honest, direct communication
• Work-life balance — we don't glorify overwork
• Continuous learning and growth
• Making a real difference in Nepali education` },
    ],
  },

  "contact-page": {
    title: "Contact Us",
    emoji: "📞",
    lastUpdated: "May 25, 2025",
    isContact: true,
    sections: [
      { h:"Get in Touch", b:`Whether you want a demo, have a question about pricing, or just want to know more about VidyaTrack — we would love to hear from you. We typically respond within 24 hours.` },
    ],
  },
};

// ── Contact card shown for the contact page ──────────────────
function ContactCard() {
  return (
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:20, marginTop:8 }}>
      {[
        { icon:<FiPhone size={20}/>, label:"Phone", value:"+977-9804044517", href:"tel:+9779804044517" },
        { icon:<FiMail  size={20}/>, label:"Email", value:"titaniumcode.np@gmail.com", href:"mailto:titaniumcode.np@gmail.com" },
        { icon:<FiMapPin size={20}/>, label:"Address", value:"Kathmandu, Nepal", href:null },
      ].map(c => (
        <div key={c.label} style={{ background:"#fff", borderRadius:14, padding:"20px 22px", border:"1px solid #e5e7eb", display:"flex", alignItems:"center", gap:14, boxShadow:"0 1px 4px rgba(0,0,0,.05)" }}>
          <div style={{ width:46, height:46, borderRadius:12, background:"#e8eef8", color:"#054bb4", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{c.icon}</div>
          <div>
            <div style={{ fontSize:".72rem", fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:".5px", marginBottom:4 }}>{c.label}</div>
            {c.href ? (
              <a href={c.href} style={{ fontSize:".95rem", fontWeight:600, color:"#054bb4", textDecoration:"none" }}>{c.value}</a>
            ) : (
              <div style={{ fontSize:".95rem", fontWeight:600, color:"#111827" }}>{c.value}</div>
            )}
          </div>
        </div>
      ))}
      <div style={{ gridColumn:"1/-1", background:"#054bb4", borderRadius:14, padding:"20px 22px", color:"#fff", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:"1rem", marginBottom:4 }}>Ready to get started?</div>
          <div style={{ fontSize:".85rem", color:"rgba(255,255,255,.75)" }}>Email us or call and we'll set up a free demo for your school.</div>
        </div>
        <a href="mailto:titaniumcode.np@gmail.com" style={{ background:"#fff", color:"#054bb4", padding:"10px 22px", borderRadius:10, fontWeight:700, fontSize:".88rem", textDecoration:"none", whiteSpace:"nowrap" }}>
          📧 Email Us
        </a>
      </div>
    </div>
  );
}

// ── Related links config ────────────────────────────────────
const NAV_GROUPS = {
  legal  : [
    { key:"privacy-policy",  label:"Privacy Policy" },
    { key:"terms-of-service",label:"Terms of Service" },
    { key:"cookie-policy",   label:"Cookie Policy" },
    { key:"security",        label:"Security" },
  ],
  company: [
    { key:"about",        label:"About Us" },
    { key:"blog",         label:"Blog" },
    { key:"careers",      label:"Careers" },
    { key:"contact-page", label:"Contact" },
  ],
};

function getGroup(key) {
  if (NAV_GROUPS.legal.find(p => p.key === key))   return "legal";
  if (NAV_GROUPS.company.find(p => p.key === key)) return "company";
  return "legal";
}

// ── Main component ───────────────────────────────────────────
export default function LegalPage() {
  const { page }  = useParams();
  const navigate  = useNavigate();
  const data      = PAGES[page];
  const group     = getGroup(page);
  const peerLinks = NAV_GROUPS[group];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { window.scrollTo(0, 0); }, [page]);

  useSEO({
    title: data ? data.title : "Page Not Found",
    description: data
      ? `${data.title} — VidyaTrack, the cloud-based school management system for schools in Nepal. Homework, attendance, notices & parent communication.`
      : "The page you are looking for does not exist.",
    path: `/legal/${page || ""}`,
    noindex: !data,
  });

  if (!data) {
    return (
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"Inter,sans-serif", background:"#f4f7fd" }}>
        <div style={{ textAlign:"center", padding:24 }}>
          <div style={{ fontSize:"4rem", marginBottom:16 }}>🔍</div>
          <h2 style={{ fontFamily:"'Poppins',sans-serif", marginBottom:8, color:"#111827" }}>Page Not Found</h2>
          <p style={{ color:"#6b7280", marginBottom:24 }}>The page you're looking for doesn't exist.</p>
          <Link to="/" style={{ background:"#054bb4", color:"#fff", padding:"10px 24px", borderRadius:10, fontWeight:700, fontSize:".9rem", textDecoration:"none" }}>← Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight:"100vh", background:"#f4f7fd", fontFamily:"'Inter',sans-serif" }}>

      {/* Navbar */}
      <nav style={{
        background:"#fff", borderBottom:"1px solid #e5e7eb",
        padding:"0 max(20px,calc((100vw - 1100px)/2))",
        height:60, display:"flex", alignItems:"center", justifyContent:"space-between",
        position:"sticky", top:0, zIndex:100, boxShadow:"0 1px 0 #e5e7eb",
      }}>
        <Link to="/" style={{ display:"flex", alignItems:"center", gap:10, textDecoration:"none" }}>
          <img src="/vtLight.png" alt="VidyaTrack" style={{ width:36, height:36, objectFit:"contain" }} />
          <span style={{ fontFamily:"'Poppins',sans-serif", fontWeight:800, fontSize:".95rem", color:"#054bb4" }}>VidyaTrack</span>
        </Link>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={() => navigate(-1)} style={{ display:"flex", alignItems:"center", gap:6, background:"#f3f4f6", border:"none", cursor:"pointer", padding:"7px 14px", borderRadius:8, fontSize:".82rem", fontWeight:600, color:"#374151", fontFamily:"inherit", transition:"all .15s" }}
            onMouseEnter={e=>{e.currentTarget.style.background="#e5e7eb"}} onMouseLeave={e=>{e.currentTarget.style.background="#f3f4f6"}}>
            <FiArrowLeft size={14}/> Back
          </button>
          <Link to="/" style={{ display:"flex", alignItems:"center", background:"#054bb4", color:"#fff", padding:"7px 16px", borderRadius:8, fontSize:".82rem", fontWeight:700, textDecoration:"none" }}>Home</Link>
        </div>
      </nav>

      {/* Content */}
      <div style={{ maxWidth:820, margin:"0 auto", padding:"40px 20px 80px" }}>

        {/* Hero */}
        <div style={{ marginBottom:36, display:"flex", alignItems:"center", gap:16 }}>
          <div style={{ width:56, height:56, background:"#e8eef8", borderRadius:16, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.8rem", flexShrink:0 }}>
            {data.emoji}
          </div>
          <div>
            <h1 style={{ fontFamily:"'Poppins',sans-serif", fontWeight:800, fontSize:"clamp(1.5rem,4vw,2rem)", color:"#111827", margin:0, lineHeight:1.2 }}>
              {data.title}
            </h1>
            <p style={{ fontSize:".82rem", color:"#6b7280", marginTop:6 }}>Last updated: {data.lastUpdated}</p>
          </div>
        </div>

        {/* Contact card (for contact page) */}
        {data.isContact && <ContactCard/>}

        {/* Sections */}
        <div style={{ display:"flex", flexDirection:"column", gap:20, marginTop:data.isContact?28:0 }}>
          {data.sections.map((s, i) => (
            <div key={i} style={{ background:"#fff", borderRadius:14, padding:"22px 26px", border:"1px solid #e5e7eb", boxShadow:"0 1px 4px rgba(0,0,0,.04)" }}>
              <h3 style={{ fontFamily:"'Poppins',sans-serif", fontWeight:700, fontSize:".97rem", color:"#054bb4", marginBottom:10, display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ width:4, height:18, background:"#054bb4", borderRadius:2, display:"inline-block", flexShrink:0 }}/>
                {s.h}
              </h3>
              <div style={{ fontSize:".875rem", color:"#374151", lineHeight:1.85, whiteSpace:"pre-line" }}>
                {s.b}
              </div>
            </div>
          ))}
        </div>

        {/* Related pages */}
        <div style={{ marginTop:36, padding:"20px 24px", background:"#fff", borderRadius:14, border:"1px solid #e5e7eb" }}>
          <div style={{ fontSize:".72rem", fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:".7px", marginBottom:14 }}>
            {group === "legal" ? "Other Legal Pages" : "More from VidyaTrack"}
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {peerLinks.filter(p => p.key !== page).map(p => (
              <Link key={p.key} to={`/legal/${p.key}`} style={{
                padding:"6px 16px", borderRadius:20,
                background:"#f3f4f6", border:"1px solid #e5e7eb",
                color:"#054bb4", fontSize:".82rem", fontWeight:500,
                textDecoration:"none", transition:"all .15s",
              }}
              onMouseEnter={e=>{e.currentTarget.style.background="#054bb4";e.currentTarget.style.color="#fff";e.currentTarget.style.borderColor="#054bb4";}}
              onMouseLeave={e=>{e.currentTarget.style.background="#f3f4f6";e.currentTarget.style.color="#054bb4";e.currentTarget.style.borderColor="#e5e7eb";}}
              >
                {PAGES[p.key]?.emoji} {p.label}
              </Link>
            ))}
            {/* Cross-link to the other group */}
            <Link to={group==="legal"?"/legal/about":"/legal/privacy-policy"} style={{
              padding:"6px 16px", borderRadius:20,
              background:"#e8eef8", border:"1px solid #c7d5ee",
              color:"#054bb4", fontSize:".82rem", fontWeight:500,
              textDecoration:"none", transition:"all .15s",
            }}
            onMouseEnter={e=>{e.currentTarget.style.background="#054bb4";e.currentTarget.style.color="#fff";}}
            onMouseLeave={e=>{e.currentTarget.style.background="#e8eef8";e.currentTarget.style.color="#054bb4";}}
            >
              {group==="legal"?"🏫 About VidyaTrack":"🔒 Privacy Policy"}
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ background:"#111827", color:"rgba(255,255,255,.5)", textAlign:"center", padding:"20px", fontSize:".8rem" }}>
        © {new Date().getFullYear()} VidyaTrack · Made with ❤️ in Nepal 🇳🇵 ·{" "}
        <a href="mailto:titaniumcode.np@gmail.com" style={{ color:"rgba(255,255,255,.5)", textDecoration:"none" }}>titaniumcode.np@gmail.com</a>
        <div style={{ marginTop:8, fontSize:".76rem" }}>
          Built by{" "}
          <a href="https://titaniumcode.netlify.app" target="_blank" rel="noopener noreferrer"
            style={{ color:"rgba(255,255,255,.75)", fontWeight:600, textDecoration:"none",
              padding:"3px 10px", borderRadius:6, background:"rgba(255,255,255,.06)", border:"1px solid rgba(255,255,255,.12)" }}>
            Titanium Code Pvt. Ltd ↗
          </a>
        </div>
      </div>
      <style>{`
        @media(max-width:640px){
          div[style*="grid-template-columns:1fr 1fr"]{grid-template-columns:1fr!important}
          div[style*="max-width:780px"]{padding:24px 16px 48px!important}
          div[style*="display:flex"][style*="gap:10"]{flex-wrap:wrap!important}
          div[style*="display:grid"][style*="minmax(220px"]{grid-template-columns:1fr!important}
        }
        @media(max-width:480px){
          div[style*="display:flex"][style*="gap:8"]{flex-wrap:wrap!important}
          nav{padding:0 14px!important}
        }
      `}</style>
    </div>
  );
}